import type { TFunction } from "i18next";
import type { GuesteriaAdmin } from "@/types/guesteria-admin";
import type { UserProfile } from "@/types/user-profile";
import { Role } from "@/types/user";
import type { GroupUserRow } from "@/components/pages/users/components/GroupCreateUsersTable";

export const getNameInitials = (
  firstName: string | undefined,
  lastName: string | undefined,
  fallback: string,
): string =>
  `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.trim() ||
  fallback.slice(0, 2).toUpperCase();

export const buildAdminRows = (
  admins: GuesteriaAdmin[],
  t: TFunction,
): GroupUserRow[] =>
  admins.map((admin) => {
    const fullName = `${admin.firstName ?? ""} ${admin.lastName ?? ""}`.trim();
    const displayName = fullName || admin.email || admin.username || admin.id;

    return {
      id: admin.id,
      name: displayName,
      role: t("users.table.guesteriaAdmin", "Guesteria Admin"),
      hotelGroup: t(
        "users.groups.create.adminTenantAccessSummary",
        "{{assigned}} assigned / {{created}} created",
        {
          assigned: admin.assignedTenantsCount,
          created: admin.createdTenantsCount,
        },
      ),
      initials: getNameInitials(admin.firstName, admin.lastName, displayName),
    };
  });

export const buildTenantRows = (
  users: UserProfile[],
  mapRoleLabel: (role: Role | "SUPER_ADMIN" | "GUESTERIA_ADMIN") => string,
): GroupUserRow[] =>
  users.map((user) => {
    const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
    const displayName = fullName || user.email || user.id;

    return {
      id: user.id,
      name: displayName,
      role: mapRoleLabel(user.role),
      hotelGroup: user.tenant?.name ?? "-",
      initials: getNameInitials(user.firstName, user.lastName, displayName),
    };
  });
